/* oxlint-disable @typescript-eslint/no-explicit-any */

import { captureException } from '@/src/config/sentry'

type TAsyncRetryOptions = {
  /** The maximum number of times an attempt to resolve the promise will be performed (default to 3) */
  attempts?: number
  /** Optional delay between attempts (default to 0) */
  delayBetweenAttempts?: number
}

/**
 * Promise retry pattern
 *
 * @throws Error Any error encountered during promise resolution
 * @return The result returned by the promise resolution
 */
export async function asyncRetry<F extends (attempt?: number) => Promise<any>>(
  /** The function to call */
  fn: F,
  { attempts = 3, delayBetweenAttempts = 0 }: TAsyncRetryOptions = {},
  attempt: number = 1,
): Promise<Awaited<ReturnType<F>>> {
  try {
    return await fn(attempt)
  } catch (error) {
    if (attempt >= attempts) {
      throw error
    }
    if (delayBetweenAttempts) {
      await new Promise((resolve) => setTimeout(resolve, delayBetweenAttempts))
    }
    return asyncRetry(fn, { attempts, delayBetweenAttempts }, attempt + 1)
  }
}

type TAsyncWithTimeoutOptions = {
  /** The time given to the promise to resolve before timeout */
  attemptTime: number
  /** Optional timeout error message (default to 'Timeout') */
  timeoutMessage?: string
}

/**
 * Promise timeout pattern
 *
 * @throws Any error encountered during promise resolution or timeout error
 * @return The result returned by the promise resolution
 */
export const asyncWithTimeout = async <F extends () => Promise<any>>(
  /** The function to call */
  fn: F,
  { attemptTime, timeoutMessage = 'Timeout' }: TAsyncWithTimeoutOptions,
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(timeoutMessage))
    }, attemptTime)
  })

  try {
    const result = await Promise.race([fn(), timeoutPromise])
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    return result
  } catch (e) {
    captureException.then((f) => f(e))
    throw e
  }
}

/**
 * Promise retry + timeout
 *
 * Combination or asyncRetry and asyncWithTimeout
 *
 * @see {@link asyncRetry}
 * @see {@link asyncWithTimeout}
 *
 * @throws Error Any error encountered during promise resolution or timeout error
 * @return The result returned by the promise resolution
 */
export const asyncRetryWithTimeout = <F extends (time?: number) => Promise<any>>(
  /** The function to call */
  fn: F,
  { attemptTime, timeoutMessage, attempts, delayBetweenAttempts }: TAsyncRetryOptions & TAsyncWithTimeoutOptions,
): Promise<Awaited<ReturnType<F>>> =>
  asyncRetry(() => asyncWithTimeout(async () => await fn(), { attemptTime, timeoutMessage }), {
    attempts,
    delayBetweenAttempts,
  })
